/**
 * Track a calendar event into workspaces (PRD §5.4b) — the same picker flow as
 * Candidate/Legislator/Initiative tracking, over workspace_event_tracking.
 * Works for legislative (leg:<id>) and public (evt:<uuid>) events.
 */
import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../lib/api';
import FormModal from '@shared/ui/FormModal';
import WorkspacePickerList, { PickableWorkspace } from './workspace/WorkspacePickerList';

interface EventWorkspaceSelectionModalProps {
  eventKey: string;
  eventTitle: string;
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful save so callers can refresh tracked state */
  onChanged?: () => void;
}

const EventWorkspaceSelectionModal: React.FC<EventWorkspaceSelectionModalProps> = ({
  eventKey,
  eventTitle,
  isOpen,
  onClose,
  onChanged,
}) => {
  const [workspaces, setWorkspaces] = useState<PickableWorkspace[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [initialTracked, setInitialTracked] = useState<Set<string>>(new Set());
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isOpen) return;
    const load = async () => {
      setLoading(true);
      try {
        const [wsResponse, trackingResponse] = await Promise.all([
          api.get('/workspaces'),
          api.get(`/calendar/event-tracking/${encodeURIComponent(eventKey)}`),
        ]);
        const userWorkspaces = Array.isArray(wsResponse.data) ? wsResponse.data : [];
        const trackedIn: Set<string> = trackingResponse.data?.success
          ? new Set<string>(trackingResponse.data.data.map((t: { workspace_id: string }) => t.workspace_id))
          : new Set<string>();
        setWorkspaces(userWorkspaces.map((ws: { id: string; name: string }) => ({ id: ws.id, name: ws.name })));
        setSelected(new Set<string>(trackedIn));
        setInitialTracked(new Set<string>(trackedIn));
      } catch {
        setWorkspaces([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isOpen, eventKey]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const toAdd = [...selected].filter((id) => !initialTracked.has(id));
      const toRemove = [...initialTracked].filter((id) => !selected.has(id));
      await Promise.all([
        ...toAdd.map((id) => api.post(`/workspaces/${id}/events/tracking`, { event_key: eventKey })),
        ...toRemove.map((id) =>
          api.delete(`/workspaces/${id}/events/tracking/${encodeURIComponent(eventKey)}`)
        ),
      ]);
      queryClient.invalidateQueries(['proHome', 'calendar']);
      toast.success('Event tracking updated');
      onChanged?.();
      onClose();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Failed to update tracking');
    } finally {
      setSaving(false);
    }
  };

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <FormModal
      isOpen={isOpen}
      title="Track Event"
      description={eventTitle}
      onClose={onClose}
      onSubmit={handleSave}
      submitLabel="Save"
      isSubmitting={saving}
    >
      <WorkspacePickerList
        workspaces={workspaces}
        selectedIds={[...selected]}
        onToggle={toggle}
        loading={loading}
        prompt="Tracked events appear in My Events for everyone in the workspace."
      />
    </FormModal>
  );
};

export default EventWorkspaceSelectionModal;
